import { formatDateTime, formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import { useAuditChain, type AuditBatchRecord } from "./use-integrity.ts";
import { KeyPins } from "./KeyPins.tsx";
import {
  verificationLevelStatus,
  verificationLevelLabel,
  verificationLevelTitle,
  chainStatusLevel,
  chainStatusLabel,
  chainStatusReasonLabel,
  firstFailureView,
  headPositionText,
  headHashShort,
  exportLagView,
  keyStateLabel,
  keyPolicyLabel,
  auditFailureReasonLabel,
} from "./vocab.ts";

/**
 * The audit-chain detail (task 11.9; design 12.3 "a chain row drills down to the chain detail
 * `/integrity/audit-chains/{id}`"). This is the drill-down target: when an operator expands a chain
 * row the table mounts this, which fetches `GET /api/v1/integrity/audit-chains/{id}` through
 * {@link useAuditChain} and shows the chain's full four always-present members, its key pins and a
 * page of its batches (newest first).
 *
 * It restates the four always-present members (verification level with `pending` grey-not-green,
 * head, export lag, first failure with `none` when clean) so the detail is self-describing, then
 * lists the batches with each batch's own verified level and status (`unverifiable` distinct from
 * `failed`, never green). It renders its own loading/error state so a slow detail read never blanks
 * the row that opened it.
 */

export interface AuditChainDetailViewProps {
  readonly chainId: string;
}

export function AuditChainDetailView(props: AuditChainDetailViewProps): ReactNode {
  const { query } = useAuditChain(props.chainId);

  if (query.data === undefined) {
    if (query.isError && !query.error.isWaking) {
      return (
        <div className="itg-detail__state" role="alert">
          <strong>Chain detail unavailable</strong>
          <span>The chain detail could not load. The chain summary above still applies.</span>
        </div>
      );
    }
    return (
      <div className="itg-detail__state" role="status" aria-live="polite">
        <strong>Loading chain detail…</strong>
      </div>
    );
  }

  const chain = query.data;
  const level = chain.verification_level;
  const failure = firstFailureView(chain.first_failure);
  const lag = exportLagView(chain.export_lag);
  const reason = chainStatusReasonLabel(chain.status_reason);
  const headHash = headHashShort(chain.head);

  const batchColumns: readonly Column<AuditBatchRecord>[] = [
    {
      key: "seq",
      header: "Batch",
      isRowHeader: true,
      cell: (b) => <span className="itg-num">#{b.batch_seq}</span>,
    },
    {
      key: "status",
      header: "Status",
      cell: (b) => (
        <StatusBadge
          level={chainStatusLevel(b.status)}
          label={chainStatusLabel(b.status)}
          size="sm"
        />
      ),
    },
    {
      key: "level",
      header: "Level",
      cell: (b) => (
        <StatusBadge
          level={verificationLevelStatus(b.verified_level)}
          label={verificationLevelLabel(b.verified_level)}
          title={verificationLevelTitle(b.verified_level)}
          size="sm"
        />
      ),
    },
    {
      key: "range",
      header: "Entries",
      align: "right",
      cell: (b) => (
        <span className="itg-num">
          {b.first_seq} to {b.last_seq}
        </span>
      ),
    },
    {
      key: "reason",
      header: "Failure",
      cell: (b) =>
        b.failure_reason === null ? (
          <span className="itg-faint">None</span>
        ) : (
          <span className="itg-reason">{auditFailureReasonLabel(b.failure_reason)}</span>
        ),
    },
    {
      key: "key",
      header: "Key",
      cell: (b) => (
        <span className="itg-faint">
          <code className="itg-code">{b.kid}</code>
          {b.key_state !== null && ` · ${keyStateLabel(b.key_state)}`}
          {b.accepted_during_revocation_propagation && " · accepted during revocation"}
        </span>
      ),
    },
    {
      key: "received",
      header: "Received",
      align: "right",
      cell: (b) => (
        <time
          className="itg-num"
          dateTime={isoAttr(b.received_at)}
          title={isoAttr(b.received_at)}
        >
          {formatTimeOfDay(b.received_at)}
        </time>
      ),
    },
  ];

  return (
    <div className="itg-detail">
      {/* Restate the four always-present members so the detail is self-describing. */}
      <dl className="itg-detail__facts">
        <div className="itg-detail__fact">
          <dt>Verification level</dt>
          <dd>
            <StatusBadge
              level={verificationLevelStatus(level)}
              label={verificationLevelLabel(level)}
              title={verificationLevelTitle(level)}
              size="sm"
            />
          </dd>
        </div>
        <div className="itg-detail__fact">
          <dt>Head position</dt>
          <dd>
            <span className="itg-num">{headPositionText(chain.head)}</span>
            {headHash !== null && <code className="itg-code"> {headHash}</code>}
          </dd>
        </div>
        <div className="itg-detail__fact">
          <dt>Export lag</dt>
          <dd>
            <span className="itg-num" data-warning={lag.isWarning ? "true" : undefined}>
              {lag.text}
            </span>
          </dd>
        </div>
        <div className="itg-detail__fact">
          <dt>First failure</dt>
          <dd>
            {failure.kind === "none" ? (
              <StatusBadge
                level="healthy"
                label="None"
                title="No first failure: the chain is clean so far."
                size="sm"
              />
            ) : (
              <span className="itg-reason">
                {failure.reasonLabel}
                {failure.batchSeq !== null && ` · batch #${failure.batchSeq}`} ·{" "}
                <time dateTime={isoAttr(failure.detectedAtMs)}>
                  {formatDateTime(failure.detectedAtMs)}
                </time>
              </span>
            )}
          </dd>
        </div>
        <div className="itg-detail__fact">
          <dt>Status</dt>
          <dd>
            <StatusBadge
              level={chainStatusLevel(chain.status)}
              label={chainStatusLabel(chain.status)}
              size="sm"
            />
            {reason !== null && <span className="itg-faint"> · {reason}</span>}
          </dd>
        </div>
        <div className="itg-detail__fact">
          <dt>Key policy</dt>
          <dd>
            <span className="itg-faint">{keyPolicyLabel(chain.key_policy)}</span>
          </dd>
        </div>
      </dl>

      <div className="itg-detail__block">
        <h3 className="itg-detail__subtitle">Keys (pinned vs observed)</h3>
        <KeyPins pins={chain.pins} />
      </div>

      <div className="itg-detail__block">
        <h3 className="itg-detail__subtitle">Batches (newest first)</h3>
        <DenseTable
          caption="Audit batches for this chain, newest first"
          columns={batchColumns}
          rows={chain.batches}
          rowKey={(b) => String(b.batch_seq)}
          stickyHeader
          emptyMessage="No batches have been received for this chain yet."
        />
      </div>
    </div>
  );
}
